import CloseOutlined from '@mui/icons-material/CloseOutlined';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { DebounceField } from '@openx/components-core/lib/DebounceField/DebounceField.tsx';
import { FiltersContextProvider } from '@openx/components-filters/lib/context/FiltersContext.tsx';
import type { FiltersContextProviderProps } from '@openx/components-filters/lib/context/types.ts';
import { FiltersBar } from '@openx/components-filters/lib/FiltersBar/FiltersBar.tsx';
import { SelectableTable } from '@openx/components-table/SelectableTable/SelectableTable.tsx';
import { Table, TableName } from '@openx/components-table/Table.tsx';
import type { AudienceSegmentEntry, InventoryBundle, InventoryDomain } from '@openx/types/gqlTypes.ts';
import type { SelectOperation } from '@openx/types/selectableTable.ts';
import { CriteriaDimension } from '@openx/types/tableCriteria.ts';
import type { BaseRow } from '@openx/types/tableHelpers.ts';
import { applySelectionChange } from '@openx/utils/lib/applySelectionChange.ts';
import { usePermissionContext } from '@openx/utils/permission/context/PermissionContext.tsx';
import type { TFunction } from 'i18next';
import isEqual from 'lodash/isEqual';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDealPrompt } from '../../hooks/useDealPrompt.ts';
import { useDealPromptPermission } from '../../hooks/useDealPromptPermission.ts';
import { getTableFiltersConfig } from '../../utils/filters/tableFiltersConfig.tsx';
import { type DealPromptContextType, type DealPromptFilters, dealPromptContextTypes } from '../../utils/types.ts';
import { getNextAudienceSelectionOnToggleAll } from './getNextAudienceSelectionOnToggleAll.ts';
import { PricingSection } from './PricingSection.tsx';
import { useDealPromptDetailsTableConfig } from './table/useDealPromptDetailsTableConfig.tsx';
import { useDealPromptDetailsTableResults } from './table/useDealPromptDetailsTableResults.ts';
import { UseOnlyFreeSegmentsSwitch } from './UseOnlyFreeSegmentsSwitch.tsx';
import { useSelectAudienceItems } from './useSelectAudienceItems.ts';

type DealPromptDetailsItemsTableSectionProps = {
  items: InventoryDomain[] | InventoryBundle[] | AudienceSegmentEntry[];
};

const getUiBitsPerContext = (t: TFunction, contextType: DealPromptContextType, resultsCount: number) => {
  const MAP = {
    [dealPromptContextTypes.DOMAINS]: {
      resultsTitle: t('Domain Results [ {count} ]', { count: resultsCount }),
      searchPlaceholder: t('Search for Domains'),
      tableName: TableName.DEAL_PROMPT_DOMAINS,
    },
    [dealPromptContextTypes.APPS]: {
      resultsTitle: t('App Results [ {count} ]', { count: resultsCount }),
      searchPlaceholder: t('Search for Apps'),
      tableName: TableName.DEAL_PROMPT_BUNDLES,
    },
    [dealPromptContextTypes.AUDIENCE]: {
      resultsTitle: t('Segments Results [ {count} ]', { count: resultsCount }),
      searchPlaceholder: t('Search for Segments'),
      tableName: TableName.DEAL_PROMPT_AUDIENCES,
    },
  };

  return MAP[contextType] ?? MAP[dealPromptContextTypes.DOMAINS];
};

const TableComponent = SelectableTable(Table);

export const DealPromptDetailsItemsTableSection = ({ items }: DealPromptDetailsItemsTableSectionProps) => {
  const { t } = useTranslation();
  const config = useDealPromptDetailsTableConfig();
  const { isInternalUser } = useDealPromptPermission();
  const [useOnlyFreeSegments, setUseOnlyFreeSegments] = useState(false);
  const [disabledItems, setDisabledItems] = useState<BaseRow[]>([]);
  const { session } = usePermissionContext();
  const currentAccountId = session?.account.external_id;

  const {
    state: { selectedItems, contextType, selectedAudienceItems, selectedTile },
    setSelectedItems,
    setSelectedAudienceItems,
  } = useDealPrompt();

  const correctSelectedItems = useMemo(() => {
    return contextType === dealPromptContextTypes.AUDIENCE ? selectedAudienceItems : selectedItems;
  }, [contextType, selectedAudienceItems, selectedItems]);

  const correctItemsSetter = useMemo(() => {
    return contextType === dealPromptContextTypes.AUDIENCE ? setSelectedAudienceItems : setSelectedItems;
  }, [contextType, setSelectedAudienceItems, setSelectedItems]);

  const uiBitsPerContext = useMemo(
    () => getUiBitsPerContext(t, contextType, items?.length),
    [t, contextType, items?.length]
  );

  const [search, setSearch] = useState<string>('');
  const { results, totalResults, tableCriteria, handleCriteriaChange } = useDealPromptDetailsTableResults(
    items,
    search
  );

  useSelectAudienceItems(items, tableCriteria, correctItemsSetter);

  const handleToggleSelect = useCallback(
    (newlySelectedItems: BaseRow[], operation: SelectOperation) => {
      let changeItems = newlySelectedItems;

      if (contextType === dealPromptContextTypes.APPS && newlySelectedItems.length === 1) {
        const allItemsWithSameId = results.filter(result => result.id === newlySelectedItems[0].id);
        if (allItemsWithSameId.length > 1) {
          changeItems = allItemsWithSameId;
        }
      }

      const selectedItemsNext = applySelectionChange({
        changeItems,
        itemsComparer: isEqual,
        operation,
        previouslySelectedItems: correctSelectedItems,
      });

      correctItemsSetter(selectedItemsNext);
    },
    [correctSelectedItems, correctItemsSetter, contextType, results]
  );

  const handleToggleAll = useCallback(() => {
    if (correctSelectedItems.length) {
      correctItemsSetter([]);
      return;
    }
    correctItemsSetter(items as (InventoryDomain[] | InventoryBundle[]) & AudienceSegmentEntry[]);
  }, [items, correctSelectedItems, correctItemsSetter]);

  const handleToggleAllAudience = useCallback(() => {
    correctItemsSetter(
      getNextAudienceSelectionOnToggleAll({
        allItems: items as AudienceSegmentEntry[],
        visibleItems: totalResults as AudienceSegmentEntry[],
        selectedItems: correctSelectedItems as AudienceSegmentEntry[],
      }) as (InventoryDomain[] | InventoryBundle[]) & AudienceSegmentEntry[]
    );
  }, [items, totalResults, correctSelectedItems, correctItemsSetter]);

  const correctToggleAll = useMemo(() => {
    return contextType === dealPromptContextTypes.AUDIENCE ? handleToggleAllAudience : handleToggleAll;
  }, [contextType, handleToggleAllAudience, handleToggleAll]);

  const itemsCountLabel = useMemo(
    () =>
      correctSelectedItems.length
        ? t('{selected} / {total} items selected', { selected: correctSelectedItems.length, total: items.length })
        : t('{count} items', { count: items.length }),
    [items, correctSelectedItems, t]
  );

  const handleUseOnlyFreeSegmentsChange = useCallback(
    (checked: boolean) => {
      setUseOnlyFreeSegments(checked);
      if (contextType !== dealPromptContextTypes.AUDIENCE) return;

      const audienceItems = items as AudienceSegmentEntry[];
      if (checked) {
        const freeSegments = audienceItems.filter(item => !item.sellable || item.account_id === currentAccountId);
        const disabledItems = audienceItems.filter(item => !item.sellable || item.account_id !== currentAccountId);
        setDisabledItems(disabledItems);
        correctItemsSetter(freeSegments as (InventoryDomain[] | InventoryBundle[]) & AudienceSegmentEntry[]);
      } else {
        const withoutFree = audienceItems as (InventoryDomain[] | InventoryBundle[]) & AudienceSegmentEntry[];
        setDisabledItems([]);
        correctItemsSetter(withoutFree);
      }
    },
    [contextType, items, correctItemsSetter, currentAccountId]
  );

  const onFiltersChange = useCallback(
    (value: DealPromptFilters) => {
      handleCriteriaChange({
        dimension: CriteriaDimension.FILTERS,
        value,
      });
    },
    [handleCriteriaChange]
  );

  const getAccountOptions = useCallback(() => {
    const seen = new Set<string>();
    return (items as AudienceSegmentEntry[])
      .map(item => ({
        name: item.account_name,
        id: item.account_id,
      }))
      .filter(({ id }) => {
        if (seen.has(id)) return false;
        seen.add(id);
        return true;
      });
  }, [items]);

  const filtersProviderConfig: FiltersContextProviderProps<DealPromptFilters>['config'] = {
    filtersConfig: getTableFiltersConfig({
      t,
      accountOptions: getAccountOptions(),
    }),
    visibleFilters: ['account'],
    disabledFilters: [],
    criteria: tableCriteria,
    onFiltersChange,
    hideClearFiltersButton: !isInternalUser,
  };

  const hasSelectedAtLeastOneSellableSegment = useMemo(() => {
    if (contextType !== dealPromptContextTypes.AUDIENCE) return false;
    return selectedAudienceItems.some(item => item.sellable && item.account_id !== session?.account.external_id);
  }, [contextType, selectedAudienceItems, session?.account.external_id]);

  const selectedSegmentSet = useMemo(() => {
    return contextType === dealPromptContextTypes.AUDIENCE &&
      selectedTile?.item != null &&
      'normalized_definition' in selectedTile.item
      ? selectedTile.item
      : null;
  }, [contextType, selectedTile]);

  const isUseOnlyFreeSegmentsDisabled = useMemo(() => {
    return tableCriteria?.filters?.account?.length != null && tableCriteria?.filters?.account?.length > 0;
  }, [tableCriteria?.filters?.account]);

  return (
    <FiltersContextProvider config={filtersProviderConfig}>
      <Stack>
        {hasSelectedAtLeastOneSellableSegment && !useOnlyFreeSegments && (
          <>
            <PricingSection selectedAudienceItems={selectedAudienceItems} selectedSegmentSet={selectedSegmentSet} />
            <Divider />
          </>
        )}

        <Box sx={{ px: 4, pt: 4, pb: 3 }}>
          <Typography data-test="details-view-table-results-count" variant="h2">
            {uiBitsPerContext.resultsTitle}
          </Typography>
        </Box>

        <Divider />

        <Box sx={{ width: '100%', bgcolor: 'background.boxLight', px: 4, py: 1.5 }}>
          <Stack direction="row" spacing={1}>
            <DebounceField
              value={search}
              onChange={({ target: { value } }) => setSearch(value)}
              variant="outlined"
              size="small"
              placeholder={uiBitsPerContext.searchPlaceholder}
              sx={{ width: '100%', maxWidth: '555px' }}
              slotProps={{
                input: {
                  endAdornment: search && (
                    <IconButton
                      aria-label={t('Clear search')}
                      onClick={() => setSearch('')}
                      data-test="details-view-table-search-clear"
                      size="small"
                    >
                      <CloseOutlined />
                    </IconButton>
                  ),
                },
                htmlInput: { 'data-test': 'details-view-table-search-input' },
              }}
            />
            {contextType === dealPromptContextTypes.AUDIENCE && <FiltersBar />}

            <UseOnlyFreeSegmentsSwitch
              isDisabled={isUseOnlyFreeSegmentsDisabled}
              checked={useOnlyFreeSegments}
              onChange={handleUseOnlyFreeSegmentsChange}
            />
          </Stack>
        </Box>

        <Divider />

        <Box sx={{ px: 4, py: 1 }}>
          <Typography data-test="items-count-label" variant="body1" sx={{ color: 'text.secondary' }}>
            {itemsCountLabel}
          </Typography>
        </Box>

        <Divider />

        <TableComponent
          name={uiBitsPerContext.tableName}
          columns={config}
          criteria={tableCriteria}
          onCriteriaChange={handleCriteriaChange}
          data={results}
          loading={false}
          selectedItems={correctSelectedItems}
          disabledItems={disabledItems}
          toggleSelect={handleToggleSelect}
          onToggleAll={correctToggleAll}
          enableTableHeaderCustomization={false}
          getRowKey={(row, index) => `${row.id}-${index}`}
        />
      </Stack>
    </FiltersContextProvider>
  );
};
